// Hook مشترک تأیید مدیر (AP-IAM-03 §5) برای عملیات مالی بیش از سقف (مصرف‌کنندگان: صندوق و صورتحساب AP-BIL-01/02):
// approvals:check ← Allowed بی‌درنگ؛ RequiresApproval ← ApprovalDialog (مدیر حاضر با موبایل و PIN ← approvals:in-person؛ یا
// درخواست ناهمگام ← approvals). خروجی approval_id را عملیات مالی همراه درخواست خود می‌فرستد تا سرور با ConsumeAsync مصرف کند.
import { unwrap, type ApprovalCheckResult } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { ApprovalDialog, type ApprovalInPersonInput } from '@phoenix/ui';
import { useCallback, useRef, useState, type ReactNode } from 'react';

export interface ApprovalRequestInput {
  key: string;
  amount: number;
  /** زمینه عملیات (مثل شناسه صورتحساب) — بدون داده بالینی یا هویتی. */
  context?: Record<string, string>;
  /** شرح نمایشی بالای دیالوگ. */
  summary?: ReactNode;
}

export type ApprovalOutcome =
  | { status: 'allowed'; check: ApprovalCheckResult }
  | { status: 'approved'; approvalId: string }
  | { status: 'requested'; approvalId: string }
  | { status: 'cancelled' };

interface PendingApproval {
  input: ApprovalRequestInput;
  resolve: (outcome: ApprovalOutcome) => void;
}

export function useApproval(): {
  requireApproval: (input: ApprovalRequestInput) => Promise<ApprovalOutcome>;
  dialog: ReactNode;
} {
  const { tenantApi } = useTenantSession();
  const [pending, setPending] = useState<PendingApproval | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [requestedId, setRequestedId] = useState<string | null>(null);
  const pendingRef = useRef<PendingApproval | null>(null);

  const finish = useCallback((outcome: ApprovalOutcome) => {
    pendingRef.current?.resolve(outcome);
    pendingRef.current = null;
    setPending(null);
    setBusy(false);
    setError(undefined);
    setRequestedId(null);
  }, []);

  const requireApproval = useCallback(
    async (input: ApprovalRequestInput): Promise<ApprovalOutcome> => {
      const check = await unwrap(
        tenantApi.POST('/api/v1/identity/approvals:check', { body: { key: input.key, amount: input.amount } }),
      );
      if (check.decision === 'Allowed') {
        return { status: 'allowed', check };
      }
      return new Promise<ApprovalOutcome>((resolve) => {
        const next = { input, resolve };
        pendingRef.current = next;
        setError(undefined);
        setRequestedId(null);
        setPending(next);
      });
    },
    [tenantApi],
  );

  const approveInPerson = async ({ approverMobile, pin }: ApprovalInPersonInput) => {
    if (!pending) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const ticket = await unwrap(
        tenantApi.POST('/api/v1/identity/approvals:in-person', {
          body: {
            key: pending.input.key,
            amount: pending.input.amount,
            approverMobile,
            pin,
            context: pending.input.context ?? null,
            reason: null,
          },
        }),
      );
      finish({ status: 'approved', approvalId: ticket.approvalId });
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  };

  const sendRequest = async (reason: string) => {
    if (!pending) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const ticket = await unwrap(
        tenantApi.POST('/api/v1/identity/approvals', {
          body: {
            key: pending.input.key,
            amount: pending.input.amount,
            reason,
            context: pending.input.context ?? null,
          },
        }),
      );
      setRequestedId(ticket.approvalId);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const dialog = (
    <ApprovalDialog
      open={pending !== null}
      summary={pending?.input.summary}
      busy={busy}
      error={error}
      requested={requestedId !== null}
      onApproveInPerson={(input) => void approveInPerson(input)}
      onRequest={(reason) => void sendRequest(reason)}
      onCancel={() =>
        finish(requestedId ? { status: 'requested', approvalId: requestedId } : { status: 'cancelled' })
      }
    />
  );

  return { requireApproval, dialog };
}
